import React, { useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "../supabase/supabase";

// "Email me when next season opens", on the signed-out Landing page.
//
// Double opt-in (migration 081): submitting only queues a confirmation email,
// and the address joins the list when its owner opens the link in it. So the
// success state says to check the inbox, never that they are on the list.
//
// join_mailing_list answers 'ok' for a new address and an existing one alike,
// so this screen cannot be used to learn who is subscribed.

const EMAIL_SHAPE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

const COPY = {
  invalid: "Enter a valid email address.",
  busy: "Too many sign ups right now. Try again in an hour.",
  failed: "That did not go through. Try again in a minute.",
};

export default function MailingListSignup({ style }) {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | sent
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    const value = email.trim();
    if (!EMAIL_SHAPE.test(value) || value.length > 254) {
      setError(COPY.invalid);
      return;
    }
    setError(null);
    setStatus("sending");
    try {
      const { data, error: rpcError } = await supabase.rpc("join_mailing_list", { p_email: value });
      if (rpcError) throw rpcError;
      if (data === "ok") {
        setStatus("sent");
        return;
      }
      setStatus("idle");
      setError(COPY[data] || COPY.failed);
    } catch {
      setStatus("idle");
      setError(COPY.failed);
    }
  };

  if (status === "sent") {
    return (
      <div style={style} role="status">
        <p style={{ fontSize: 14, lineHeight: 1.45, color: "var(--color-text-strong)", margin: 0 }}>
          <strong style={{ display: "block", marginBottom: 2 }}>Check your inbox.</strong>
          We sent a link to {email.trim()}. Open it to confirm, and you are on the list.
        </p>
      </div>
    );
  }

  return (
    <form onSubmit={submit} noValidate style={style}>
      <p style={{ fontSize: 15, fontWeight: 700, color: "var(--color-text-strong)", margin: "0 0 2px" }}>
        Next season
      </p>
      <p style={{ fontSize: 13, lineHeight: 1.4, color: "var(--color-text-muted)", margin: "0 0 10px" }}>
        Get one email when next season opens. Nothing else.
      </p>
      <div style={{ display: "flex", gap: 8 }}>
        <input
          type="email"
          inputMode="email"
          autoComplete="email"
          placeholder="you@example.com"
          aria-label="Email address"
          aria-invalid={Boolean(error)}
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={{
            flex: 1,
            minWidth: 0,
            border: `1.5px solid ${error ? "var(--color-danger-text)" : "var(--color-border)"}`,
            borderRadius: 12,
            padding: "12px 14px",
            fontSize: 16, // 16px keeps iOS from zooming the page on focus
            fontFamily: "inherit",
            color: "var(--color-text-strong)",
            backgroundColor: "var(--color-bg-card)",
          }}
        />
        <button
          type="submit"
          disabled={status === "sending"}
          style={{
            border: 0,
            borderRadius: 12,
            backgroundColor: status === "sending" ? "var(--color-cta-disabled)" : "var(--color-cta)",
            color: "var(--color-cta-text)",
            fontWeight: 700,
            fontSize: 15,
            padding: "0 16px",
            fontFamily: "inherit",
            whiteSpace: "nowrap",
          }}
        >
          {status === "sending" ? "Sending" : "Notify me"}
        </button>
      </div>
      {error ? (
        <p role="alert" style={{ fontSize: 13, color: "var(--color-danger-text)", margin: "6px 0 0" }}>
          {error}
        </p>
      ) : (
        <p style={{ fontSize: 12, lineHeight: 1.4, color: "var(--color-text-faint)", margin: "8px 0 0" }}>
          Unsubscribe any time. See the{" "}
          <Link to="/privacy" style={{ color: "inherit" }}>Privacy Policy</Link>.
        </p>
      )}
    </form>
  );
}
